import { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { FullCourse } from 'server/types';
import { AsyncForm, InputLabel, Subtext } from '@components/async-form';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { showToast } from '@components/toaster';
import { showError } from '@components/app-error';
import { HeadingSecondary } from '@course/components/headings';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { Toggle } from '@components/toggle';
import { IcoCheck, IcoChevronDown, IcoChevronUp, IcoPlus, IcoTrash } from '@components/icons';
import { URLS } from 'shared/urls';
import { betsInUseMessage } from 'shared/tracker-people';
import { channelsLockedMessage, familyInUseMessage } from 'shared/tracker-leads';
import {
  isOpeningStep,
  LEADS_PRESET,
  matchingTrackerPreset,
  MAX_CHANNELS,
  MAX_FAMILIES,
  MAX_FUNNEL_STAGES,
  MAX_GAP_DAYS,
  MAX_LABEL_LENGTH,
  MAX_SEQUENCE_STEPS,
  MAX_SHORT_LENGTH,
  MAX_STEPS_STAGES,
  participantChannels,
  RIS_PRESET,
  ROLLUP_LABEL_NAMES,
  RollupLabelKey,
  sequenceChannel,
  sequencePreview,
  slugKey,
  sequenceUsageMessage,
  stageUsageMessage,
  StageRole,
  TrackerChannel,
  TrackerConfig,
  TrackerConfigError,
  trackerConfigKeys,
  TrackerFieldKey,
  TRACKER_PRESETS,
  TrackerPresetId,
  TrackerStage,
  validateTrackerConfig,
} from 'shared/tracker-config';

const store = rpx.courseSettings;

type TrackerSettingsData = Awaited<ReturnType<typeof store.getTrackerSettings>>;

const ROLE_CAPTIONS: Partial<Record<StageRole, string>> = {
  contacted: 'first touch',
  replied: 'a reply',
  booked: 'booking',
  held: 'conversation held',
  decision: 'decision',
  won: 'close',
  replied_no: 'a reply',
  lost: 'lost',
};

const FIXED_RULES = [
  'One row per person.',
  'Every touch and step has a date.',
  'The coach sees everything while the immersion sprint runs.',
  'Weekly rollups of the stages below.',
];

const FIELD_LABELS: Array<{ key: TrackerFieldKey; label: string }> = [
  { key: 'offer', label: 'Offer' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'segment', label: 'Segment' },
  { key: 'tier', label: 'Tier' },
  { key: 'note', label: 'Note' },
];

const CARD = 'bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5 min-w-0';
const ICON_BTN = 'p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 shrink-0';

type RemovedStage = { channelKey: string; index: number; stage: TrackerStage };

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function errorId(path: string) {
  return `tracker-err-${path.replace(/\./g, '-')}`;
}

function fieldId(path: string) {
  return `tracker-${path.replace(/\./g, '-')}`;
}

/**
 * Channels and stages added in this session hold a placeholder key while
 * editing, so inputs keep focus. At save the placeholder becomes a slug of the
 * label. Saved keys, preset keys and keys participants use never change.
 */
function refreshNewKeys(config: TrackerConfig, fixed: Set<string>): TrackerConfig {
  const taken = new Set<string>(trackerConfigKeys(config).filter((k) => fixed.has(k)));
  function keyFor(current: string, label: string, fallback: string, prefix?: string) {
    if (fixed.has(current)) {
      return current;
    }
    const next = slugKey(label || fallback, taken, prefix);
    taken.add(next);
    return next;
  }
  const channels = config.channels.map((channel) => {
    const key = keyFor(channel.key, channel.label, 'channel');
    return {
      ...channel,
      key,
      stages: channel.stages.map((s) => ({ ...s, key: keyFor(s.key, s.label, 'stage', key) })),
      exits: channel.exits.map((s) => ({ ...s, key: keyFor(s.key, s.label, 'stage', key) })),
    };
  });
  const sequence = config.sequence.map((step) => ({
    ...step,
    key: keyFor(step.key, step.name, 'step'),
  }));
  return { ...config, channels, sequence };
}

let placeholderCount = 0;

function placeholderKey() {
  placeholderCount += 1;
  return `new_${Date.now()}_${placeholderCount}`;
}

function move<T>(items: T[], index: number, offset: number) {
  const target = index + offset;
  if (target < 0 || target >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
}

function FieldError({ path, errors }: { path: string; errors: TrackerConfigError[] }) {
  const error = errors.find((e) => e.path === path);
  if (!error) {
    return;
  }
  return (
    <p id={errorId(path)} class="text-sm text-red-700 mt-1">
      {error.message}
    </p>
  );
}

function inputClass(path: string, errors: TrackerConfigError[], extra = '') {
  const invalid = errors.some((e) => e.path === path);
  return `ruz-input w-full ${invalid ? 'border-red-500' : ''} ${extra}`;
}

function describedBy(path: string, errors: TrackerConfigError[]) {
  return errors.some((e) => e.path === path) ? errorId(path) : undefined;
}

function Card({ children }: { children: ComponentChildren }) {
  return <div class={CARD}>{children}</div>;
}

export function TrackerSettings({
  course,
  onUpdate,
}: {
  course: FullCourse;
  onUpdate?: (patch: Partial<FullCourse>) => void;
}) {
  const [settings, setSettings] = useAsyncState(async () => {
    try {
      return await store.getTrackerSettings({ courseId: course.id });
    } catch (err) {
      showError(err);
    }
  }, [course.id]);

  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  const [draft, setDraft] = useState<TrackerConfig | undefined>(undefined);
  const [removed, setRemoved] = useState<RemovedStage[]>([]);
  const [blocked, setBlocked] = useState<Record<string, string>>({});
  const [confirmPreset, setConfirmPreset] = useState<TrackerPresetId | undefined>(undefined);
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<TrackerConfigError[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [focusId, setFocusId] = useState<string | undefined>(undefined);
  const [betsBlocked, setBetsBlocked] = useState(false);

  useEffect(() => {
    if (!focusId) {
      return;
    }
    document.getElementById(focusId)?.focus();
    setFocusId(undefined);
  }, [focusId]);

  if (!settings) {
    return (
      <div class="flex justify-center">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }

  const data: TrackerSettingsData = settings;
  const config = draft ?? data.config;
  const currentEnabled = enabled ?? data.enabled;
  const fixedKeys = new Set([
    ...trackerConfigKeys(data.config),
    ...trackerConfigKeys(RIS_PRESET),
    ...trackerConfigKeys(LEADS_PRESET),
    ...Object.keys(data.stepsByStage),
    ...Object.keys(data.stepsBySequence),
    ...Object.keys(data.peopleByStage),
    ...Object.keys(data.peopleByChannel),
  ]);
  const pressed = matchingTrackerPreset(config);
  const seqChannel = sequenceChannel(config);
  const isDirty =
    currentEnabled !== data.enabled || JSON.stringify(config) !== JSON.stringify(data.config);
  const families = participantChannels(config);

  const clientErrors: TrackerConfigError[] = attempted ? validateTrackerConfig(config) : [];
  const errors = [...clientErrors, ...serverErrors];
  const bannerErrors = errors.filter(
    (e, i) => errors.findIndex((other) => other.path === e.path) === i,
  );

  function update(next: TrackerConfig) {
    setDraft(next);
    setServerErrors([]);
  }

  function updateChannel(index: number, patch: (channel: TrackerChannel) => TrackerChannel) {
    update({
      ...config,
      channels: config.channels.map((c, i) => (i === index ? patch(clone(c)) : c)),
    });
  }

  function usageInChannel(channel: TrackerChannel) {
    const steps = [...channel.stages, ...channel.exits].reduce(
      (sum, s) => sum + (data.stepsByStage[s.key] ?? 0),
      0,
    );
    return { steps, people: data.peopleByChannel[channel.key] ?? 0 };
  }

  function presetBlocked(id: TrackerPresetId) {
    const preset = TRACKER_PRESETS[id].config;
    if (preset.channelsBy !== data.config.channelsBy) {
      return Object.values(data.peopleByChannel).some((n) => n > 0);
    }
    if (participantChannels(preset)) {
      const kept = new Set(preset.channels.map((c) => c.key));
      return Object.entries(data.participantsByFamily).some(
        ([key, count]) => count > 0 && !kept.has(key),
      );
    }
    const keep = new Set(trackerConfigKeys(preset));
    const usedKeys = [
      ...Object.entries(data.stepsByStage),
      ...Object.entries(data.stepsBySequence),
      ...Object.entries(data.peopleByStage),
      ...Object.entries(data.peopleByChannel),
    ]
      .filter(([, count]) => count > 0)
      .map(([key]) => key);
    return usedKeys.some((key) => !keep.has(key));
  }

  function applyPreset(id: TrackerPresetId) {
    const preset = clone(TRACKER_PRESETS[id].config);
    update({
      ...preset,
      fields: config.fields,
      bets: preset.bets || (config.bets && data.peopleUnderBets > 0),
    });
    setRemoved([]);
    setBlocked({});
    setConfirmPreset(undefined);
  }

  function choosePreset(id: TrackerPresetId) {
    if (pressed === id) {
      return;
    }
    setConfirmPreset(id);
  }

  function removeStage(channelIndex: number, stageIndex: number) {
    const channel = config.channels[channelIndex];
    const stage = channel.stages[stageIndex];
    const message = families
      ? undefined
      : stageUsageMessage(stage.key, stage.label.trim() || stage.key, data);
    if (message) {
      setBlocked({ ...blocked, [stage.key]: message });
      return;
    }
    setRemoved([...removed, { channelKey: channel.key, index: stageIndex, stage }]);
    updateChannel(channelIndex, (c) => ({
      ...c,
      stages: c.stages.filter((_, i) => i !== stageIndex),
    }));
  }

  function undoRemove(item: RemovedStage) {
    const channelIndex = config.channels.findIndex((c) => c.key === item.channelKey);
    setRemoved(removed.filter((r) => r !== item));
    if (channelIndex < 0) {
      return;
    }
    updateChannel(channelIndex, (c) => {
      const stages = [...c.stages];
      stages.splice(Math.min(item.index, stages.length), 0, item.stage);
      return { ...c, stages };
    });
  }

  function addStage(channelIndex: number) {
    updateChannel(channelIndex, (c) => ({
      ...c,
      stages: [...c.stages, { key: placeholderKey(), label: '' }],
    }));
  }

  function addChannel() {
    if (config.channels.length >= (families ? MAX_FAMILIES : MAX_CHANNELS)) {
      return;
    }
    const next: TrackerConfig = {
      ...config,
      channels: [
        ...config.channels,
        {
          key: placeholderKey(),
          label: '',
          flow: 'steps',
          stages: [{ key: placeholderKey(), label: '' }],
          exits: [],
        },
      ],
    };
    update(next);
    setFocusId(fieldId(`channels.${next.channels.length - 1}.label`));
  }

  function removeChannel(channelIndex: number) {
    update({ ...config, channels: config.channels.filter((_, i) => i !== channelIndex) });
  }

  function updateSequence(next: TrackerConfig['sequence']) {
    update({ ...config, sequence: next });
  }

  function focusFirstError() {
    const first = bannerErrors[0];
    if (!first) {
      return;
    }
    const target =
      document.getElementById(fieldId(first.path)) ?? document.getElementById(errorId(first.path));
    target?.focus();
    target?.scrollIntoView({ block: 'center' });
  }

  async function submit() {
    setAttempted(true);
    setServerErrors([]);
    const next = refreshNewKeys(config, fixedKeys);
    if (validateTrackerConfig(next).length) {
      return;
    }

    setIsSaving(true);
    try {
      const saved = await store.setTrackerSettings({
        courseId: course.id,
        enabled: currentEnabled,
        config: next,
        revision: data.revision,
      });
      setSettings(saved);
      onUpdate?.({ trackerEnabled: saved.enabled });
      setEnabled(undefined);
      setDraft(undefined);
      setRemoved([]);
      setBlocked({});
      setConfirmPreset(undefined);
      setAttempted(false);
      showToast({ title: 'Tracker settings saved', message: '', type: 'ok' });
    } catch (err) {
      // Same parsing as the standup settings: a Boom 400 carries a statusCode
      // and, from this endpoint, the field errors in data.errors; a schema
      // rejection carries type 'validation'. Anything else goes to the dialog.
      const { statusCode, type, message, data } = (err || {}) as {
        statusCode?: number;
        type?: string;
        message?: string;
        data?: { errors?: Array<{ path?: string | Array<string | number>; message?: string }> };
      };
      const fieldErrors = (data?.errors ?? [])
        .filter((e) => e.message)
        .map((e) => ({
          path: Array.isArray(e.path) ? e.path.join('.') : (e.path ?? 'server'),
          message: e.message!,
        }));
      if (
        (fieldErrors.length || message) &&
        (statusCode === 400 || statusCode === 409 || type === 'validation')
      ) {
        setServerErrors(
          fieldErrors.length
            ? fieldErrors
            : [{ path: statusCode === 409 ? 'conflict' : 'server', message: message! }],
        );
        return;
      }
      throw err;
    } finally {
      setIsSaving(false);
    }
  }

  const blockedPresets = (Object.keys(TRACKER_PRESETS) as TrackerPresetId[]).filter(
    (id) => id !== pressed && presetBlocked(id),
  );
  const peopleTotal = Object.values(data.peopleByChannel).reduce((sum, n) => sum + n, 0);

  return (
    <AsyncForm class="flex flex-col gap-8" onSubmit={submit}>
      <Card>
        <HeadingSecondary>Tracker</HeadingSecondary>
        <label class="flex items-start gap-3 cursor-pointer">
          <Toggle checked={currentEnabled} onClick={() => setEnabled(!currentEnabled)} />
          <span class="flex flex-col leading-snug">
            <strong>Turn on the tracker</strong>
            <span class="opacity-75 text-sm">Adds a Tracker tab to the course navigation.</span>
            {currentEnabled && (
              <span class="opacity-75 text-sm">
                Turning it off hides the tab. Nothing is deleted.
              </span>
            )}
          </span>
        </label>
        <div class="flex flex-col gap-2">
          <strong class="text-sm text-gray-700">The same in every immersion sprint</strong>
          <ul class="flex flex-col gap-1 text-sm">
            {FIXED_RULES.map((rule) => (
              <li key={rule} class="flex items-center gap-2">
                <IcoCheck class="w-4 h-4 text-green-600 shrink-0" />
                {rule}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <Card>
        <HeadingSecondary>Start from</HeadingSecondary>
        <div role="group" aria-label="Start from" class="flex flex-wrap gap-2">
          {(Object.keys(TRACKER_PRESETS) as TrackerPresetId[]).map((id) => (
            <Button
              key={id}
              type="button"
              aria-pressed={pressed === id}
              disabled={blockedPresets.includes(id)}
              class={`px-3 py-1.5 rounded-full border text-sm disabled:opacity-40 ${
                pressed === id
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => choosePreset(id)}
            >
              {TRACKER_PRESETS[id].label}
            </Button>
          ))}
        </div>
        {blockedPresets.some(
          (id) => TRACKER_PRESETS[id].config.channelsBy !== data.config.channelsBy,
        ) ? (
          <Subtext>{channelsLockedMessage(peopleTotal)}</Subtext>
        ) : blockedPresets.length ? (
          <Subtext>
            {data.loggedTotal
              ? `Participants have logged ${data.loggedTotal} ${
                  data.loggedTotal === 1 ? 'step' : 'steps'
                }. Rename stages instead of switching.`
              : `Participants have added ${peopleTotal} ${
                  peopleTotal === 1 ? 'person' : 'people'
                }. Rename stages instead of switching.`}
          </Subtext>
        ) : (
          <Subtext>Fills in the stages and scoreboard below. Everything stays editable.</Subtext>
        )}
        {confirmPreset && !blockedPresets.includes(confirmPreset) && (
          <div class="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
            <span class="grow">
              Replace the stages, sequence and scoreboard with the{' '}
              {TRACKER_PRESETS[confirmPreset].name} set?
            </span>
            <BtnPrimary type="button" onClick={() => applyPreset(confirmPreset)}>
              Replace
            </BtnPrimary>
            <BtnSecondary type="button" onClick={() => setConfirmPreset(undefined)}>
              Keep mine
            </BtnSecondary>
          </div>
        )}
      </Card>

      <Card>
        <HeadingSecondary>{families ? 'Channel families' : 'Stages'}</HeadingSecondary>
        {families && (
          <Subtext>Each participant copies a family and names its stages in week two.</Subtext>
        )}
        <FieldError path="channels" errors={errors} />
        {config.channels.map((channel, ci) =>
          channel.flow === 'sequence' ? (
            <SequenceChannelEditor
              key={channel.key}
              channel={channel}
              index={ci}
              errors={errors}
              onChange={(patch) => updateChannel(ci, patch)}
            />
          ) : (
            <StepsChannelEditor
              key={channel.key}
              channel={channel}
              index={ci}
              errors={errors}
              isOnly={config.channels.length <= 1}
              usage={usageInChannel(channel)}
              familyUse={families ? (data.participantsByFamily[channel.key] ?? 0) : undefined}
              blocked={blocked}
              removed={removed.filter((r) => r.channelKey === channel.key)}
              onChange={(patch) => updateChannel(ci, patch)}
              onRemoveStage={(si) => removeStage(ci, si)}
              onUndo={undoRemove}
              onAddStage={() => addStage(ci)}
              onRemoveChannel={() => removeChannel(ci)}
            />
          ),
        )}
        {config.channels.length < (families ? MAX_FAMILIES : MAX_CHANNELS) && (
          <div>
            <BtnSecondary type="button" onClick={addChannel}>
              <IcoPlus class="w-4 h-4 mr-1" />
              {families ? 'Add family' : 'Add channel'}
            </BtnSecondary>
          </div>
        )}

        {seqChannel && (
          <>
            <div class="flex flex-col gap-2">
              <InputLabel>Weekly rollups</InputLabel>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(Object.keys(ROLLUP_LABEL_NAMES) as RollupLabelKey[]).map((key) => {
                  const path = `rollupLabels.${key}`;
                  return (
                    <div key={key} class="flex flex-col gap-1 min-w-0">
                      <label class="text-sm text-gray-600" for={fieldId(path)}>
                        {ROLLUP_LABEL_NAMES[key]}
                      </label>
                      <input
                        id={fieldId(path)}
                        class={inputClass(path, errors)}
                        type="text"
                        maxLength={MAX_LABEL_LENGTH}
                        value={config.rollupLabels[key]}
                        aria-describedby={describedBy(path, errors)}
                        onInput={(e) =>
                          update({
                            ...config,
                            rollupLabels: {
                              ...config.rollupLabels,
                              [key]: (e.target as HTMLInputElement).value,
                            },
                          })
                        }
                      />
                      <FieldError path={path} errors={errors} />
                    </div>
                  );
                })}
              </div>
            </div>

            <SequenceEditor
              sequence={config.sequence}
              errors={errors}
              usage={data}
              blocked={blocked}
              onBlocked={(key, message) => setBlocked({ ...blocked, [key]: message })}
              onChange={updateSequence}
            />
          </>
        )}
      </Card>

      <Card>
        <HeadingSecondary>Scoreboard</HeadingSecondary>
        {config.scoreboard.kind === 'revenue' ? (
          <>
            <strong class="text-sm">Revenue against a target</strong>
            <Subtext>
              Each participant sets a target, and chooses at the start whether a close counts when
              money is booked or collected.
            </Subtext>
          </>
        ) : (
          <>
            <strong class="text-sm">Cost per lead</strong>
            <Subtext>Each participant sets their own destination, hourly rate and target.</Subtext>
          </>
        )}
        <FieldError path="scoreboard.kind" errors={errors} />
      </Card>

      <Card>
        <HeadingSecondary>Fields</HeadingSecondary>
        <div class="flex flex-col gap-2 text-sm">
          <label class="flex items-center gap-2 opacity-75">
            <input type="checkbox" checked disabled />
            First name
            <span class="text-gray-500 text-xs">Always</span>
          </label>
          {FIELD_LABELS.filter((field) => !(config.bets && field.key === 'offer')).map((field) => (
            <label key={field.key} class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.fields[field.key]}
                onChange={() =>
                  update({
                    ...config,
                    fields: { ...config.fields, [field.key]: !config.fields[field.key] },
                  })
                }
              />
              {field.label}
            </label>
          ))}
        </div>
        <Subtext>Never required. Hidden fields keep their data.</Subtext>
        <label class="flex items-start gap-3 cursor-pointer">
          <Toggle
            checked={config.bets}
            onClick={() => {
              if (config.bets && data.peopleUnderBets > 0) {
                setBetsBlocked(true);
                return;
              }
              setBetsBlocked(false);
              update({ ...config, bets: !config.bets });
            }}
          />
          <span class="flex flex-col leading-snug">
            <strong>Bets</strong>
            <span class="text-sm text-gray-600">
              Participants list 1–3 bets and put each person under one.
            </span>
          </span>
        </label>
        {betsBlocked && (
          <p id={fieldId('bets')} class="text-sm text-red-700" role="alert">
            {betsInUseMessage(data.peopleUnderBets)}
          </p>
        )}
        <FieldError path="bets" errors={errors} />
      </Card>

      <Card>
        <HeadingSecondary>When the immersion sprint ends</HeadingSecondary>
        <dl class="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
          <dt class="font-semibold text-gray-700">Coach access</dt>
          <dd>
            {data.endDate ? (
              <>
                Ends {dayjs(data.endDate).format('ddd D MMM YYYY')}, the standup end date.{' '}
                <a
                  class="text-indigo-600 underline"
                  href={URLS.guide.coursePage({ courseId: course.id, page: 'settings/checkins' })}
                >
                  Standups settings
                </a>
              </>
            ) : (
              'No end date. Set one in Standups to close coach access.'
            )}
          </dd>
          <dt class="font-semibold text-gray-700">Participants</dt>
          <dd>Keep their tracker, and can download it.</dd>
          <dt class="font-semibold text-gray-700">Mirasee</dt>
          <dd>Keeps the data for analysis across cohorts.</dd>
        </dl>
      </Card>

      {bannerErrors.length > 0 && (
        <div
          role="alert"
          class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex flex-col gap-1"
        >
          <strong>
            {bannerErrors.every((e) => e.path === 'conflict')
              ? 'Not saved'
              : bannerErrors.length === 1
                ? '1 thing to fix before saving'
                : `${bannerErrors.length} things to fix before saving`}
          </strong>
          {serverErrors.length > 0 ? (
            serverErrors.map((e) => <span key={e.path + e.message}>{e.message}</span>)
          ) : (
            <a
              href={`#${fieldId(bannerErrors[0].path)}`}
              class="underline"
              onClick={(e) => {
                e.preventDefault();
                focusFirstError();
              }}
            >
              {bannerErrors[0].message}
            </a>
          )}
        </div>
      )}

      <footer class="flex items-center gap-3">
        <BtnPrimary disabled={isSaving}>{isSaving ? 'Saving…' : 'Save'}</BtnPrimary>
        {isDirty && <span class="text-sm text-gray-600">Unsaved changes</span>}
      </footer>
    </AsyncForm>
  );
}

function SequenceChannelEditor({
  channel,
  index,
  errors,
  onChange,
}: {
  channel: TrackerChannel;
  index: number;
  errors: TrackerConfigError[];
  onChange: (patch: (channel: TrackerChannel) => TrackerChannel) => void;
}) {
  const base = `channels.${index}`;
  const name = channel.label.trim() || 'Outreach';

  function stageRow(stage: TrackerStage, i: number, list: 'stages' | 'exits', label: string) {
    const path = `${base}.${list}.${i}.label`;
    return (
      <li key={stage.key} class="flex flex-col gap-1 min-w-0">
        <div class="flex items-start sm:items-center gap-2">
          <span class="w-6 pt-2 sm:pt-0 text-right text-sm text-gray-500 shrink-0">
            {list === 'stages' ? `${i + 1}.` : ''}
          </span>
          <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 grow min-w-0">
            <input
              id={fieldId(path)}
              class={inputClass(path, errors)}
              type="text"
              maxLength={MAX_LABEL_LENGTH}
              value={stage.label}
              aria-label={label}
              aria-describedby={describedBy(path, errors)}
              onInput={(e) =>
                onChange((c) => {
                  c[list][i].label = (e.target as HTMLInputElement).value;
                  return c;
                })
              }
            />
            <span class="text-xs text-gray-500 sm:w-28 sm:shrink-0">
              {stage.role ? ROLE_CAPTIONS[stage.role] : ''}
            </span>
          </div>
        </div>
        <div class="pl-8">
          <FieldError path={path} errors={errors} />
        </div>
      </li>
    );
  }

  return (
    <div class="flex flex-col gap-4">
      <FormRow label="Channel name" path={`${base}.label`} errors={errors}>
        <input
          id={fieldId(`${base}.label`)}
          class={inputClass(`${base}.label`, errors)}
          type="text"
          maxLength={MAX_LABEL_LENGTH}
          value={channel.label}
          aria-describedby={describedBy(`${base}.label`, errors)}
          onInput={(e) => onChange((c) => ({ ...c, label: (e.target as HTMLInputElement).value }))}
        />
      </FormRow>
      <div class="flex flex-col gap-2">
        <InputLabel>Stages, in order</InputLabel>
        <Subtext>Rename only. The order and number are fixed.</Subtext>
        <ol class="flex flex-col gap-2">
          {channel.stages.map((stage, i) =>
            stageRow(stage, i, 'stages', `${name} stage ${i + 1} name`),
          )}
        </ol>
        <FieldError path={`${base}.stages`} errors={errors} />
      </div>
      <div class="flex flex-col gap-2">
        <InputLabel>Side exits</InputLabel>
        <ul class="flex flex-col gap-2">
          {channel.exits.map((stage, i) =>
            stageRow(stage, i, 'exits', `${name} side exit ${i + 1} name`),
          )}
        </ul>
        <FieldError path={`${base}.exits`} errors={errors} />
      </div>
    </div>
  );
}

function FormRow({
  label,
  path,
  errors,
  children,
}: {
  label: string;
  path: string;
  errors: TrackerConfigError[];
  children: ComponentChildren;
}) {
  return (
    <div class="flex flex-col gap-1 min-w-0">
      <label class="text-sm text-gray-700" for={fieldId(path)}>
        {label}
      </label>
      {children}
      <FieldError path={path} errors={errors} />
    </div>
  );
}

function StepsChannelEditor({
  channel,
  index,
  errors,
  isOnly,
  usage,
  familyUse,
  blocked,
  removed,
  onChange,
  onRemoveStage,
  onUndo,
  onAddStage,
  onRemoveChannel,
}: {
  channel: TrackerChannel;
  index: number;
  errors: TrackerConfigError[];
  isOnly: boolean;
  usage: { steps: number; people: number };
  /** A channel family: the participants who copied it. Undefined on a course-mapped channel. */
  familyUse?: number;
  blocked: Record<string, string>;
  removed: RemovedStage[];
  onChange: (patch: (channel: TrackerChannel) => TrackerChannel) => void;
  onRemoveStage: (stageIndex: number) => void;
  onUndo: (item: RemovedStage) => void;
  onAddStage: () => void;
  onRemoveChannel: () => void;
}) {
  const base = `channels.${index}`;
  const name = channel.label.trim() || `Channel ${index + 1}`;
  const isFamily = familyUse !== undefined;
  const maxStages = isFamily ? MAX_FUNNEL_STAGES : MAX_STEPS_STAGES;
  const removeTooltip = isOnly
    ? isFamily
      ? 'The tracker needs at least one channel family.'
      : 'The tracker needs at least one channel.'
    : isFamily
      ? familyUse
        ? familyInUseMessage(familyUse, name)
        : undefined
      : usage.steps
        ? 'Steps are logged in this channel. Rename it instead.'
        : usage.people
          ? 'People are in this channel. Rename it instead.'
          : undefined;

  return (
    <div class="flex flex-col gap-3 border-t border-gray-200 pt-4 first:border-t-0 first:pt-0">
      <div class="flex flex-wrap items-end gap-3">
        <div class="grow min-w-0">
          <FormRow
            label={isFamily ? 'Family name' : 'Channel name'}
            path={`${base}.label`}
            errors={errors}
          >
            <input
              id={fieldId(`${base}.label`)}
              class={inputClass(`${base}.label`, errors)}
              type="text"
              maxLength={MAX_LABEL_LENGTH}
              value={channel.label}
              placeholder={isFamily ? 'e.g. Networking' : 'e.g. LinkedIn'}
              aria-describedby={describedBy(`${base}.label`, errors)}
              onInput={(e) =>
                onChange((c) => ({ ...c, label: (e.target as HTMLInputElement).value }))
              }
            />
          </FormRow>
        </div>
        <span title={removeTooltip}>
          <BtnSecondary type="button" disabled={!!removeTooltip} onClick={onRemoveChannel}>
            {isFamily ? 'Remove family' : 'Remove channel'}
          </BtnSecondary>
        </span>
      </div>

      <div>
        <ol class="flex flex-col gap-3">
          {channel.stages.map((stage, i) => {
            const labelPath = `${base}.stages.${i}.label`;
            const shortPath = `${base}.stages.${i}.short`;
            const bookingId = fieldId(`${base}.stages.${i}.booking`);
            const otherBooking = channel.stages.some((s, j) => j !== i && s.role === 'booked');
            return (
              <li key={stage.key} class="flex flex-col gap-1">
                <div class="flex items-start sm:items-center gap-2">
                  <span class="w-6 pt-2 sm:pt-0 text-right text-sm text-gray-500 shrink-0">
                    {i + 1}.
                  </span>
                  <div class="flex flex-col sm:flex-row gap-2 grow min-w-0">
                    <input
                      id={fieldId(labelPath)}
                      class={inputClass(labelPath, errors)}
                      type="text"
                      maxLength={MAX_LABEL_LENGTH}
                      value={stage.label}
                      placeholder="Stage name"
                      aria-label={`${name} stage ${i + 1} name`}
                      aria-describedby={describedBy(labelPath, errors)}
                      onInput={(e) =>
                        onChange((c) => {
                          c.stages[i].label = (e.target as HTMLInputElement).value;
                          return c;
                        })
                      }
                    />
                    <input
                      id={fieldId(shortPath)}
                      class={inputClass(shortPath, errors, 'sm:max-w-40')}
                      type="text"
                      maxLength={MAX_SHORT_LENGTH}
                      value={stage.short ?? ''}
                      placeholder={isFamily ? 'Short name' : 'Rollup label'}
                      aria-label={`${name} stage ${i + 1} ${isFamily ? 'short name' : 'rollup label'}`}
                      aria-describedby={describedBy(shortPath, errors)}
                      onInput={(e) =>
                        onChange((c) => {
                          c.stages[i].short = (e.target as HTMLInputElement).value;
                          return c;
                        })
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    class={ICON_BTN}
                    disabled={i === 0}
                    aria-label={`Move ${name} stage ${i + 1} up`}
                    onClick={() => onChange((c) => ({ ...c, stages: move(c.stages, i, -1) }))}
                  >
                    <IcoChevronUp class="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    class={ICON_BTN}
                    disabled={i === channel.stages.length - 1}
                    aria-label={`Move ${name} stage ${i + 1} down`}
                    onClick={() => onChange((c) => ({ ...c, stages: move(c.stages, i, 1) }))}
                  >
                    <IcoChevronDown class="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    class={ICON_BTN}
                    disabled={channel.stages.length <= 1}
                    aria-label={`Remove ${name} stage ${i + 1}`}
                    onClick={() => onRemoveStage(i)}
                  >
                    <IcoTrash class="w-4 h-4" />
                  </Button>
                </div>
                {!isFamily && (
                  <label
                    for={bookingId}
                    class={`pl-8 flex items-center gap-2 text-sm ${
                      otherBooking ? 'opacity-60' : 'cursor-pointer'
                    }`}
                  >
                    <input
                      id={bookingId}
                      type="checkbox"
                      checked={stage.role === 'booked'}
                      disabled={otherBooking}
                      onChange={() =>
                        onChange((c) => {
                          c.stages[i].role = c.stages[i].role === 'booked' ? undefined : 'booked';
                          if (!c.stages[i].role) {
                            delete c.stages[i].role;
                          }
                          return c;
                        })
                      }
                    />
                    Counts as a booking (early warning)
                  </label>
                )}
                <div class="pl-8">
                  <FieldError path={labelPath} errors={errors} />
                  <FieldError path={shortPath} errors={errors} />
                  {blocked[stage.key] && (
                    <p class="text-sm text-amber-800 mt-1">{blocked[stage.key]}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
      <FieldError path={`${base}.stages`} errors={errors} />
      {removed.map((item) => (
        <p key={item.stage.key} class="text-sm text-gray-700">
          Removed “{item.stage.label.trim() || 'Untitled'}”.{' '}
          <button type="button" class="text-indigo-600 underline" onClick={() => onUndo(item)}>
            Undo
          </button>
        </p>
      ))}
      {channel.stages.length < maxStages && (
        <div>
          <BtnSecondary type="button" onClick={onAddStage}>
            <IcoPlus class="w-4 h-4 mr-1" />
            Add stage
          </BtnSecondary>
        </div>
      )}
    </div>
  );
}

function SequenceEditor({
  sequence,
  errors,
  usage,
  blocked,
  onBlocked,
  onChange,
}: {
  sequence: TrackerConfig['sequence'];
  errors: TrackerConfigError[];
  usage: TrackerSettingsData;
  blocked: Record<string, string>;
  onBlocked: (key: string, message: string) => void;
  onChange: (next: TrackerConfig['sequence']) => void;
}) {
  function remove(index: number) {
    const step = sequence[index];
    const message = sequenceUsageMessage(step.key, step.name.trim() || step.key, usage);
    if (message) {
      onBlocked(step.key, message);
      return;
    }
    onChange(sequence.filter((_, j) => j !== index));
  }

  function patch(index: number, change: Partial<TrackerConfig['sequence'][number]>) {
    onChange(sequence.map((s, i) => (i === index ? { ...s, ...change } : s)));
  }

  return (
    <div class="flex flex-col gap-2">
      <InputLabel>Touch sequence</InputLabel>
      <Subtext>
        Steps with 0 days are opening touches: a person gets one of them, then the later steps in
        turn.
      </Subtext>
      <div class="hidden sm:flex gap-2 text-sm font-medium text-gray-600">
        <span class="grow">Step</span>
        <span class="w-48">Days after the last touch</span>
        <span class="w-28">
          <span class="sr-only">Actions</span>
        </span>
      </div>
      <ol class="flex flex-col gap-3 sm:gap-2 text-sm">
        {sequence.map((step, i) => {
          const namePath = `sequence.${i}.name`;
          const daysPath = `sequence.${i}.gapDays`;
          return (
            <li key={step.key} class="flex flex-col gap-1">
              <div class="flex flex-col sm:flex-row sm:items-start gap-2">
                <div class="grow min-w-0">
                  <input
                    id={fieldId(namePath)}
                    class={inputClass(namePath, errors)}
                    type="text"
                    maxLength={MAX_LABEL_LENGTH}
                    value={step.name}
                    placeholder="Step name"
                    aria-label={`Touch sequence step ${i + 1} name`}
                    aria-describedby={describedBy(namePath, errors)}
                    onInput={(e) => patch(i, { name: (e.target as HTMLInputElement).value })}
                  />
                  <FieldError path={namePath} errors={errors} />
                </div>
                <div class="flex items-start gap-2">
                  <div class="grow sm:grow-0 sm:w-48">
                    <div class="flex items-center gap-2">
                      <div class="w-20 shrink-0">
                        <input
                          id={fieldId(daysPath)}
                          class={inputClass(daysPath, errors)}
                          type="number"
                          min={0}
                          max={MAX_GAP_DAYS}
                          value={String(step.gapDays)}
                          aria-label={`Touch sequence step ${i + 1} days after the last touch`}
                          aria-describedby={describedBy(daysPath, errors)}
                          onInput={(e) => {
                            const value = Number((e.target as HTMLInputElement).value);
                            patch(i, { gapDays: Number.isFinite(value) ? Math.trunc(value) : 0 });
                          }}
                        />
                      </div>
                      <span class="text-gray-600 sm:whitespace-nowrap">
                        {isOpeningStep(step)
                          ? 'Opening touch'
                          : step.gapDays === 1
                            ? 'day'
                            : 'days'}
                      </span>
                    </div>
                    <FieldError path={daysPath} errors={errors} />
                  </div>
                  <div class="flex shrink-0 w-28">
                    <Button
                      type="button"
                      class={ICON_BTN}
                      disabled={i === 0}
                      aria-label={`Move step ${i + 1} up`}
                      onClick={() => onChange(move(sequence, i, -1))}
                    >
                      <IcoChevronUp class="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      class={ICON_BTN}
                      disabled={i === sequence.length - 1}
                      aria-label={`Move step ${i + 1} down`}
                      onClick={() => onChange(move(sequence, i, 1))}
                    >
                      <IcoChevronDown class="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      class={ICON_BTN}
                      disabled={sequence.length <= 1}
                      aria-label={`Remove step ${i + 1}`}
                      onClick={() => remove(i)}
                    >
                      <IcoTrash class="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              {blocked[step.key] && <p class="text-sm text-amber-800">{blocked[step.key]}</p>}
            </li>
          );
        })}
      </ol>
      <FieldError path="sequence" errors={errors} />
      {sequence.length < MAX_SEQUENCE_STEPS && (
        <div>
          <BtnSecondary
            type="button"
            onClick={() => onChange([...sequence, { key: placeholderKey(), name: '', gapDays: 7 }])}
          >
            <IcoPlus class="w-4 h-4 mr-1" />
            Add step
          </BtnSecondary>
        </div>
      )}
      <p class="text-sm text-gray-600">{sequencePreview(sequence)}</p>
    </div>
  );
}
